// Unified product-analytics client (design: docs/design/unified_analytics.md).
// Patterns cloned from src/telemetry/reporter.js: buffered queue, periodic +
// pagehide flush via sendBeacon, batch dedup ids, self-disabling on repeated
// failures. Analytics is sacrificial: nothing here may ever break the page.
//
// Capture surfaces:
//   - declarative clicks:  <a data-track="cta.clicked" data-track-props='{"cta":"upgrade_header"}'>
//   - imperative API:      window.TBAnalytics.track(name, props) / .page()
//   - rage clicks:         3+ clicks on the same element within RAGE_WINDOW_MS
//   - HTML5 validation:    invalid events on form fields
//
// Identity is server-side (session cookie, or the signed anon token embedded
// in <meta name="analytics-token"> on pre-auth signup pages). The server only
// accepts registry events flagged client-emittable.

const ENDPOINT = '/analytics/events';
const FLUSH_MS = 10000;
const MAX_QUEUE = 20;
const MAX_FAILURES = 3;
const RAGE_WINDOW_MS = 700;
const RAGE_CLICKS = 3;

function uuid() {
	if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
	});
}

function meta(name) {
	const el = document.querySelector(`meta[name="${name}"]`);
	return el ? el.getAttribute('content') : null;
}

class AnalyticsTracker {
	constructor() {
		this.queue = [];
		this.failures = 0;
		this.disabled = false;
		this.page = meta('analytics-page') || null;
		this.token = meta('analytics-token') || null;
		this.csrf = meta('csrf-token') || null;
		this.timer = setInterval(() => this.flush(), FLUSH_MS);
		this.lastClicks = { key: null, times: [] };
		this.bind();
	}

	track(name, props) {
		if (this.disabled || typeof name !== 'string') return;
		this.queue.push({ name, props: props || {}, page: this.page });
		if (this.queue.length >= MAX_QUEUE) this.flush();
	}

	trackPage() {
		if (!this.page) return;
		this.track('page.viewed', { page: this.page, referrer_page: document.referrer ? 'external' : undefined });
	}

	bind() {
		document.addEventListener('click', (e) => this.onClick(e), true);
		document.addEventListener('invalid', (e) => this.onInvalid(e), true);
		const finalFlush = () => this.flush(true);
		window.addEventListener('pagehide', finalFlush);
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') finalFlush();
		});
	}

	onClick(e) {
		try {
			const tracked = e.target && e.target.closest && e.target.closest('[data-track]');
			if (tracked) {
				let props = {};
				try {
					props = JSON.parse(tracked.getAttribute('data-track-props') || '{}');
				} catch (err) { /* malformed attribute: track without props */ }
				this.track(tracked.getAttribute('data-track'), props);
			}
			this.detectRage(e);
		} catch (err) { /* never break the page */ }
	}

	detectRage(e) {
		const el = e.target && e.target.closest ? (e.target.closest('a, button, [role="button"], input, label') || e.target) : e.target;
		if (!el || !el.tagName) return;
		const key = el.tagName + '#' + (el.id || '') + '.' + (typeof el.className === 'string' ? el.className.split(' ')[0] : '');
		const now = Date.now();
		if (this.lastClicks.key !== key) this.lastClicks = { key, times: [] };
		this.lastClicks.times = this.lastClicks.times.filter((t) => now - t < RAGE_WINDOW_MS);
		this.lastClicks.times.push(now);
		if (this.lastClicks.times.length >= RAGE_CLICKS) {
			this.lastClicks.times = [];
			this.track('ux.rage_click', { page: this.page, selector: key.slice(0, 120) });
		}
	}

	onInvalid(e) {
		try {
			const field = e.target;
			const form = field && field.form;
			this.track('ux.form.validation_error', {
				page: this.page,
				form: (form && (form.id || form.getAttribute('action'))) || 'unknown',
				field: field.name || field.id || field.type || 'unknown'
			});
		} catch (err) { /* ignore */ }
	}

	flush(final = false) {
		if (this.disabled || this.queue.length === 0) return;
		const events = this.queue.splice(0, MAX_QUEUE);
		const body = JSON.stringify({ batch_id: uuid(), token: this.token || undefined, csrf: this.csrf || undefined, events });

		if (final && navigator.sendBeacon) {
			navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
			return;
		}
		fetch(ENDPOINT, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'same-origin',
			keepalive: final,
			body
		}).then((res) => {
			if (res.ok) {
				this.failures = 0;
			} else {
				this.fail(res.status === 403 || res.status === 404);
			}
		}).catch(() => this.fail(false));
	}

	fail(hard) {
		this.failures += 1;
		if (hard || this.failures >= MAX_FAILURES) {
			this.disabled = true;
			clearInterval(this.timer);
		}
	}
}

export default function initAnalytics() {
	if (window.TBAnalytics) return window.TBAnalytics;
	const tracker = new AnalyticsTracker();
	window.TBAnalytics = {
		track: (name, props) => tracker.track(name, props),
		page: () => tracker.trackPage()
	};
	tracker.trackPage();
	return window.TBAnalytics;
}
